import React from 'react';
import classNames from 'classnames/bind';
import { useToggle } from '@mantine/hooks';

import { Camera } from 'types/camera';
import {
  CameraDiscoveryRequest,
  DiscoveredCamera,
} from 'types/camera_discovery_request';

import { isNotNullOrUndefined } from 'services/nullable';
import { useCameraDiscoveryRequest } from 'gateways/hooks/useCameraDiscoveryRequest';
import { useLinkedCameras } from 'gateways/hooks/useLinkedCameras';
import { useSet } from 'hooks/useSet';

import { AuthenticateCameraDialog } from 'cameras/components/AuthenticateCameraDialog/AuthenticateCameraDialog';
import { Box } from 'components/Box/Box';
import { BulkLinkGatewayCamerasDialog } from 'cameras/components/LinkGatewayCameraDialog/BulkLinkGatewayCamerasDialog';
import { Button } from 'components/Button/Button';
import { DiscoveredCameraCard } from 'cameras/components/DiscoveredCameraCard/DiscoveredCameraCard';
import { FormMessage } from 'components/FormMessage/FormMessage';
import { Heading } from 'components/Heading/Heading';
import { LinkGatewayCameraDialog } from 'cameras/components/LinkGatewayCameraDialog/LinkGatewayCameraDialog';
import { LinkedCameraCard } from 'cameras/components/LinkedCameraCard/LinkedCameraCard';
import { Separator } from 'components/Separator/Separator';
import { Text } from 'components/Text/Text';

import styles from './DiscoveredAndLinkedGatewayCameras.module.scss';

const c = classNames.bind(styles);

type DiscoveredAndLinkedGatewayCamerasProps = {
  gatewayId: string;
  discoveryRequest?: CameraDiscoveryRequest;
  discover: () => void;
};

export function DiscoveredAndLinkedGatewayCameras({
  gatewayId,
  discoveryRequest,
  discover,
}: DiscoveredAndLinkedGatewayCamerasProps) {
  const { data: linkedCameras } = useLinkedCameras(gatewayId);

  const { data: discoveryRequestResult } = useCameraDiscoveryRequest(
    gatewayId,
    discoveryRequest?.id
  );

  const [selectedCameras, { add, remove, clear }] = useSet<DiscoveredCamera>();
  const [camerasToLink, setCamerasToLink] =
    React.useState<DiscoveredCamera[]>();
  const [isLinkingOpen, toggleIsLinkingOpen] = useToggle();

  const [cameraToUpdate, setCameraToUpdate] = React.useState<Camera>();
  const [isAuthOpen, toggleIsAuthOpen] = useToggle();

  if (!discoveryRequestResult) {
    return null;
  }

  if (discoveryRequestResult.status === 'pending') {
    return (
      <Box className={c('discovering-cameras-box')}>
        <div className="loading-spinner xsmall" />
        <div className={c('discovering-cameras-text')}>
          <Heading level="3">Discovering cameras</Heading>
          <Text type="paragraph">
            Lumeo is searching for ONVIF and USB cameras accessible by this
            gateway...
          </Text>
        </div>
      </Box>
    );
  }

  const result = discoveryRequestResult.result;

  const hasDiscoveredCameras =
    isNotNullOrUndefined(result) && Array.isArray(result) && result.length > 0;
  const hasLinkedCameras = linkedCameras && linkedCameras.data.length > 0;

  return (
    <>
      {hasDiscoveredCameras && (
        <section className={c('cameras')}>
          <div className={c('cameras-header')}>
            <Heading level="2">Discovered Cameras</Heading>
            <Button
              size="small"
              className={c('button-wrap')}
              onClick={discover}
            >
              Discover
            </Button>
          </div>

          <div className={c('cameras-list')}>
            <ul>
              {result.map((camera) => (
                <li
                  className={c('cameras-list-item')}
                  key={displaySource(camera)}
                >
                  <DiscoveredCameraCard
                    key={displaySource(camera)}
                    camera={camera}
                    displaySource={displaySource}
                    onLinkButtonClick={() => {
                      setCamerasToLink([camera]);
                      toggleIsLinkingOpen();
                    }}
                    isSelected={selectedCameras.includes(camera)}
                    onSelect={(isSelected) => {
                      if (isSelected) {
                        add(camera);
                      } else {
                        remove(camera);
                      }
                    }}
                  />
                </li>
              ))}
            </ul>

            {selectedCameras.length > 0 && (
              <div className={c('cameras-bulk-link')}>
                <Text>
                  {selectedCameras.length} camera
                  {selectedCameras.length > 1 ? 's' : ''} selected
                </Text>
                <Button size="small" variant="secondary" onClick={clear}>
                  Clear selection
                </Button>
                <Button
                  className={c('cameras-bulk-link-primary')}
                  size="small"
                  variant="primary"
                  onClick={() => {
                    setCamerasToLink([...selectedCameras]);
                    toggleIsLinkingOpen();
                  }}
                >
                  Link selected cameras
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {!hasDiscoveredCameras && (
        <>
          <Box className={c('no-cameras-box')}>
            <div>
              <Heading level="4">No cameras</Heading>
              <Text type="paragraph">
                No cameras found. Please make sure your camera is connected and
                then retry discovery.
              </Text>
            </div>
            <div className={c('button-wrap')}>
              <Button size="small" variant="primary" onClick={discover}>
                Discover
              </Button>
            </div>
          </Box>

          <FormMessage intent="info" icon="info">
            If your IP camera cannot be discovered automatically, but it is on
            the same subnet, check to ensure you have enabled ONVIF in your
            camera's settings.
          </FormMessage>
        </>
      )}

      {hasLinkedCameras && (
        <>
          {hasDiscoveredCameras && <Separator />}
          <section className={c('cameras')}>
            <Heading level="2" className={c('cameras-header')}>
              Linked Cameras
              {!hasDiscoveredCameras && (
                <Button
                  size="small"
                  className={c('button-wrap')}
                  onClick={discover}
                >
                  Discover
                </Button>
              )}
            </Heading>
            <ul className={c('cameras-list')}>
              {linkedCameras.data.map((camera) => (
                <li className={c('cameras-list-item')} key={camera.id}>
                  <LinkedCameraCard
                    camera={camera}
                    displaySource={displaySource}
                    onAuthenticate={() => {
                      setCameraToUpdate(camera);
                      toggleIsAuthOpen();
                    }}
                  />
                </li>
              ))}
            </ul>
          </section>
        </>
      )}

      <LinkGatewayCameraDialog
        gatewayId={gatewayId}
        camera={camerasToLink?.[0]}
        onOpenChange={toggleIsLinkingOpen}
        onSuccess={remove}
        open={camerasToLink && camerasToLink.length === 1 && isLinkingOpen}
      />

      <BulkLinkGatewayCamerasDialog
        gatewayId={gatewayId}
        cameras={camerasToLink}
        onSuccess={clear}
        onOpenChange={toggleIsLinkingOpen}
        open={camerasToLink && camerasToLink.length > 1 && isLinkingOpen}
      />

      <AuthenticateCameraDialog
        camera={cameraToUpdate}
        open={Boolean(cameraToUpdate) && isAuthOpen}
        onOpenChange={toggleIsAuthOpen}
      />
    </>
  );
}

function displaySource(camera: DiscoveredCamera) {
  if (camera.ip_local) {
    return `${camera.model || ''} @ ${camera.ip_local}`;
  }

  if (camera.ip_ext) {
    return `${camera.model || ''} @ ${camera.ip_ext}`;
  }

  return camera.uri || '';
}
